package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.example.mystybag.appCore.logic.pages.StatementsViewModel
import com.example.mystybag.appCore.model.pages.StatementRowDisplay
import com.example.mystybag.appCore.model.pages.StatementsUIState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.DatePicker
import com.example.mystybag.common.ui.components.native.CaptionCapitalization
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.expected.formatLocalMonthYear
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme

private const val W_BAG = 3f
private const val W_DATE = 2.2f
private const val W_AMOUNT = 2f

private val cellPaddingH = 4.dp

@Composable
fun StatementsView(viewModel: StatementsViewModel) {
    if (rememberIsWebDesktopLayout()) {
        StatementsWebView(viewModel)
        return
    }

    UIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        showTopBar = true,
        removeVerticalPadding = false
    ) { state ->
        StatementsContent(
            uiState = state.value,
            viewModel = viewModel,
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = p16, vertical = p8)
        )
    }
}

@Composable
internal fun StatementsContent(
    uiState: StatementsUIState,
    viewModel: StatementsViewModel,
    modifier: Modifier
) {
    val colors = AppTheme.current.colors
    val eur = sharedString(SR.strings.currency_eur)
    val isWebDesktop = rememberIsWebDesktopLayout()

    Column(modifier = modifier) {
        when {
            uiState.isLoading -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = colors.primary)
                }
            }
            else -> {
                Column(modifier = Modifier.fillMaxSize()) {
                    Column(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxWidth()
                            .verticalScroll(rememberScrollState())
                    ) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(p8)
                        ) {
                            DatePicker(
                                modifier = Modifier.weight(1f),
                                value = uiState.filterDateFrom,
                                onValueChange = viewModel::onFilterDateFromChanged,
                                label = sharedString(SR.strings.statements_from),
                                maxDate = uiState.filterDateTo,
                                valueDisplayOverride = uiState.filterDateFrom?.let { formatLocalMonthYear(it) }
                            )
                            DatePicker(
                                modifier = Modifier.weight(1f),
                                value = uiState.filterDateTo,
                                onValueChange = viewModel::onFilterDateToChanged,
                                label = sharedString(SR.strings.statements_to),
                                minDate = uiState.filterDateFrom,
                                valueDisplayOverride = uiState.filterDateTo?.let { formatLocalMonthYear(it) }
                            )
                        }
                        StatementsSummaryRow(
                            period = uiState.reportPeriodDisplay,
                            totalFormatted = uiState.totalAmountFormatted,
                            currency = eur
                        )
                        if (uiState.rows.isEmpty()) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = p16),
                                contentAlignment = Alignment.Center
                            ) {
                                Texts.BodyMedium(
                                    text = sharedString(SR.strings.statements_no_rows),
                                    color = colors.onSurface,
                                    textAlign = TextAlign.Center
                                )
                            }
                        } else {
                            HorizontalDivider(color = colors.outlineVariant)
                            StatementsHeaderRow()
                            HorizontalDivider(color = colors.outlineVariant)
                            uiState.rows.forEachIndexed { index, row ->
                                StatementDataRow(row = row, currency = eur)
                                if (index < uiState.rows.lastIndex) {
                                    HorizontalDivider(color = colors.outlineVariant.copy(alpha = 0.6f))
                                }
                            }
                        }
                    }
                    DefaultButton(
                        modifier = Modifier
                            .fillMaxWidth(if (isWebDesktop) 0.5f else 1f)
                            .align(Alignment.CenterHorizontally)
                            .padding(top = p12, bottom = if (isWebDesktop) 32.dp else p16),
                        caption = sharedString(SR.strings.statements_share),
                        capitalization = CaptionCapitalization.None,
                        type = DefaultButtonType.PRIMARY,
                        textColor = AppTheme.current.colors.onPrimaryContainer,
                        enabled = uiState.rows.isNotEmpty(),
                        callback = viewModel::onShareStatements
                    )
                }
            }
        }
    }
}

@Composable
private fun StatementsSummaryRow(period: String, totalFormatted: String, currency: String) {
    val theme = AppTheme.current
    val muted = theme.colors.onSurfaceVariant
    val onSurface = theme.colors.onSurface
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = p12),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Texts.BodySmall(
                text = sharedString(SR.strings.statements_selected_period),
                color = muted
            )
            Spacer(modifier = Modifier.height(4.dp))
            Texts.BodyLarge(
                text = period,
                color = onSurface,
                fontWeight = FontWeight.SemiBold
            )
        }
        Column(
            horizontalAlignment = Alignment.End,
            modifier = Modifier.padding(start = p8)
        ) {
            Texts.BodySmall(
                text = sharedString(SR.strings.statements_total_amount),
                color = muted,
                textAlign = TextAlign.End
            )
            Spacer(modifier = Modifier.height(4.dp))
            Texts.BodyLarge(
                text = "$totalFormatted $currency",
                color = onSurface,
                textAlign = TextAlign.End,
                fontWeight = FontWeight.SemiBold
            )
        }
    }
}

@Composable
private fun StatementsHeaderRow() {
    val headerBg = AppTheme.current.colors.surfaceContainerLow
    val headerFg = AppTheme.current.colors.onSurfaceVariant
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(headerBg)
            .padding(vertical = p12),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Texts.LabelLarge(
            text = sharedString(SR.strings.statements_column_bag_name),
            modifier = Modifier
                .weight(W_BAG)
                .padding(horizontal = cellPaddingH),
            color = headerFg,
            maxLines = 2,
            textAlign = TextAlign.Start
        )
        Texts.LabelLarge(
            text = sharedString(SR.strings.statements_column_sale_date),
            modifier = Modifier
                .weight(W_DATE)
                .padding(horizontal = cellPaddingH),
            color = headerFg,
            maxLines = 2,
            textAlign = TextAlign.Center
        )
        Texts.LabelLarge(
            text = sharedString(SR.strings.statements_column_amount),
            modifier = Modifier
                .weight(W_AMOUNT)
                .padding(horizontal = cellPaddingH),
            color = headerFg,
            maxLines = 2,
            textAlign = TextAlign.End
        )
    }
}

@Composable
private fun StatementDataRow(row: StatementRowDisplay, currency: String) {
    val onSurface = AppTheme.current.colors.onSurface
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = p12),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Texts.BodyMedium(
            text = row.bagName,
            modifier = Modifier
                .weight(W_BAG)
                .padding(horizontal = cellPaddingH),
            color = onSurface,
            maxLines = 2,
            textAlign = TextAlign.Start
        )
        Texts.BodyMedium(
            text = row.saleDateDisplay,
            modifier = Modifier
                .weight(W_DATE)
                .padding(horizontal = cellPaddingH),
            color = onSurface,
            maxLines = 1,
            textAlign = TextAlign.Center
        )
        Texts.BodyMedium(
            text = "${row.amountFormatted} $currency",
            modifier = Modifier
                .weight(W_AMOUNT)
                .padding(horizontal = cellPaddingH),
            color = onSurface,
            maxLines = 1,
            textAlign = TextAlign.End
        )
    }
}
